import { useCallback, useContext } from 'react';
import { useSettings } from '../contexts/settingsContext';
import { useSearch } from '../contexts/searchContext';
import { useCameraCaptures } from '../contexts/cameraCapturesContext';
import { useHistoryDrawer } from '../contexts/historyDrawerContext';
import { CognitoUserContext } from '../contexts/cognitoUserContext';
import { searchFace } from '../services/api/rekognitionService';
import { getPersonsByIds } from '../services/api/personService';
import { createMatchEvent, createGroupMatchEvent, getCurrentLocation } from '../services/api/eventService';
import { compressImage } from '../services/image/compressionService';
import { getSuggestedCompressionQuality } from '../services/image/networkSpeedService';
import { setLocalStorageEnumMap } from '../services/user/browserService';
import { uploadCapture } from '../services/s3/captureUploadService';

/**
 * Custom hook for face matching logic
 * Handles compression, network-aware quality adjustment, face search, and result processing
 * Supports both interactive (CameraModal) and silent (StealthCamera) modes
 *
 * @param {Object} options Configuration options
 * @param {boolean} options.silent - If true, operates silently without UI updates (for stealth mode)
 * @param {Function} options.onSuccess - Callback on successful match (receives result, matchedPersons)
 * @param {Function} options.onNoMatch - Callback when no match found (receives captureId, dataUrl)
 * @param {Function} options.onError - Callback on error (receives error, captureId)
 * @returns {Object} Face matching functions and state
 */
export function useFaceMatching(options = {}) {
  const { silent = false, onSuccess, onNoMatch, onError } = options;

  const settings = useSettings();
  const { cognitoUser } = useContext(CognitoUserContext);
  const {
    selectedDataset,
    setEnumMap,
    setTriggerAttributeSearch,
    setFilteredPeople,
    setMatchSource,
    setMatchAccuracy,
    setCapturedImageS3Key
  } = useSearch();
  const { updateCapture } = useCameraCaptures();
  const { openAssignments, addPendingMatchEvent } = useHistoryDrawer();

  const {
    sliderValue,
    maxFacesValue,
    compressionQuality,
    setCompressionQuality,
    autoAdjustCompression,
    updateCharacteristicsOnMatch,
    autoCreateEventOnAIMatch,
    locationHighAccuracy,
    locationAcquisitionTimeout,
    locationCacheMaxAge
  } = settings;

  /**
   * Performs face matching with network-aware compression
   * @param {string} captureId - ID of the capture to match
   * @param {string} dataUrl - Base64 image data URL
   * @returns {Promise<Object>} Match result with persons and metadata
   */
  const matchFace = useCallback(async (captureId, dataUrl) => {
    try {
      // Network-aware compression quality adjustment
      let qualityToUse = compressionQuality;

      if (autoAdjustCompression) {
        qualityToUse = getSuggestedCompressionQuality();
        setCompressionQuality(qualityToUse);
      }

      // Compress image before uploading/matching
      // Two-stage compression strategy:
      // 1. MAX_SIZE_MB: Hard limit on file size (7MB - balances quality vs upload time)
      // 2. qualityToUse: Network-aware initial quality (0.1-1.0, auto-adjusted based on connection speed)
      //    - Fast connection: Start with high quality (0.9), reduce only if needed to meet size limit
      //    - Slow connection: Start with lower quality (0.6-0.8) to reduce upload time
      const MAX_SIZE_MB = 7;
      const compressedDataUrl = await compressImage(dataUrl, MAX_SIZE_MB, qualityToUse);

      // Upload to S3 first (if user is authenticated)
      let imageS3Key = null;
      // Use Cognito sub (no special characters like + or @)
      const userId = cognitoUser?.attributes?.sub || cognitoUser?.username;

      if (userId) {
        try {
          imageS3Key = await uploadCapture(compressedDataUrl, userId);
        } catch (uploadError) {
          console.error('[Face Match] S3 upload failed, falling back to base64:', uploadError);
          // Continue with base64 if upload fails
        }
      }

      // Search for matching faces (using S3 key if available, otherwise base64)
      const result = await searchFace(
        imageS3Key ? null : compressedDataUrl, // image (base64)
        sliderValue,
        maxFacesValue,
        imageS3Key, // imageS3Key
        userId // userId (Cognito sub)
      );

      // Always treat matches as an array (0 or more matches)
      const matches = result.matches || [];
      const matchCount = matches.length;

      console.log(`[Face Match] Found ${matchCount} match(es)`);
      console.log('[Face Match] Result:', {
        matchCount,
        captureS3Key: result.captureS3Key,
        hasMatches: matches.length > 0
      });

      // Update capture with success/failure status and all bounding boxes
      updateCapture(captureId, {
        matchFailed: matchCount === 0,
        boundingBoxes: matches.map(m => m.boundingBox).filter(Boolean), // Array of all bounding boxes
        boundingBox: matches.length > 0 ? matches[0].boundingBox : null // Keep first for backward compatibility
      });

      // If we have matches, fetch person details
      let matchedPersons = [];
      if (matchCount > 0) {
        const personIds = matches.map(m => m.externalImageId);
        console.log('[Face Match] Person IDs from matches:', personIds);
        console.log('[Face Match] Unique person IDs:', [...new Set(personIds)]);
        matchedPersons = await getPersonsByIds(personIds);
        console.log('[Face Match] Retrieved person details', {
          personCount: matchedPersons.length,
          personIdsRequested: personIds.length,
          uniquePersonIds: new Set(personIds).size,
          topSimilarity: matches[0].similarity
        });
      }

      // Create match list string for display
      const matchList = matchedPersons
        .map(p => `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown')
        .join(', ');

      // Interactive mode: update UI contexts (only if NOT auto-creating events)
      if (!silent && !autoCreateEventOnAIMatch && matchCount > 0) {
        setFilteredPeople(matchedPersons);
        setMatchSource('face_match');
        setMatchAccuracy(matches[0].similarity / 100); // Use best match
        setCapturedImageS3Key(result.captureS3Key || null);

        // Update characteristics to match first/best person if enabled
        if (updateCharacteristicsOnMatch && matchedPersons.length > 0) {
          const topMatchedPerson = matchedPersons[0];

          const newEnumMap = new Map();
          newEnumMap.set('Dataset', selectedDataset);

          // Map person characteristics to enum map
          if (topMatchedPerson.gender) newEnumMap.set('Gender', topMatchedPerson.gender);
          if (topMatchedPerson.agegroup) newEnumMap.set('AgeGroup', topMatchedPerson.agegroup);
          if (topMatchedPerson.haircolor) newEnumMap.set('HairColor', topMatchedPerson.haircolor);
          if (topMatchedPerson.hairstyle) newEnumMap.set('HairStyle', topMatchedPerson.hairstyle);
          if (topMatchedPerson.facialhair) newEnumMap.set('FacialHair', topMatchedPerson.facialhair);
          if (topMatchedPerson.eyecolor) newEnumMap.set('EyeColor', topMatchedPerson.eyecolor);
          if (topMatchedPerson.skintone) newEnumMap.set('SkinTone', topMatchedPerson.skintone);

          setEnumMap(newEnumMap);
          setLocalStorageEnumMap(newEnumMap);
        }
      }

      // Auto-create events: Iterate through ALL matched persons
      if (autoCreateEventOnAIMatch && matchCount > 0) {
        // Get location once for all events
        const location = await getCurrentLocation({
          locationHighAccuracy,
          locationAcquisitionTimeout,
          locationCacheMaxAge
        }).catch(err => {
          console.error('[Face Match] Failed to get location:', err);
          return null;
        });

        const openAssignment = openAssignments.length > 0 ? openAssignments[0] : null;
        const assignmentEventId = openAssignment?.eventId || null;

        console.log('[Face Match] Open assignment context:', {
          hasOpenAssignment: !!openAssignment,
          assignmentEventId,
          assignmentType: openAssignment?.eventType,
          assignmentStatus: openAssignment?.status,
          assignmentDescription: openAssignment?.description
        });

        /**
         * Match counts explained:
         * - uniqueMatchedPersons: Number of distinct people matched (unique person IDs)
         *   Example: Photo with 3 faces, 2 match same person = 2 unique persons
         * - totalDetectedFaces: Total faces found by Rekognition (result.detectedFaceCount)
         *   Example: Photo with 3 faces = 3 detected faces
         * - matches.length: Total number of match events to create (one per face/bounding box)
         *   Example: Photo with 3 faces, 2 match same person = 3 match events
         */
        const uniqueMatchedPersons = new Set(matches.map(m => m.externalImageId)).size;
        const totalDetectedFaces = result.detectedFaceCount || null;

        console.log('[Face Match] Match counts:', {
          uniqueMatchedPersons,
          totalDetectedFaces,
          rawMatches: result.matchCount // For debugging only
        });

        const isMultiFace = totalDetectedFaces > 1;
        console.log('[Face Match] Is multi-face?', isMultiFace);

        // For multi-face matches, create a note listing all unique matched people
        const uniquePersonNames = matchedPersons.map(p =>
          `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown'
        );
        const groupNote = isMultiFace
          ? `Group photo with: ${[...new Set(uniquePersonNames)].join(', ')}`
          : null;

        // For multi-face matches, create group event FIRST
        let groupEventId = null;
        let shouldCreateIndividualEvents = true;

        if (isMultiFace) {
          // Use unique person names for the group event
          const personNames = [...new Set(uniquePersonNames)];

          try {
            console.log('[Face Match] Creating group match event with:', {
              location,
              totalMatchCount: uniqueMatchedPersons,
              totalDetectedFaceCount: totalDetectedFaces,
              captureS3Key: result.captureS3Key,
              personNames,
              assignmentEventId
            });
            const groupMatchEvent = await createGroupMatchEvent(
              location,
              uniqueMatchedPersons,  // matchCount
              totalDetectedFaces,    // detectedFaceCount
              result.captureS3Key,   // capturedImageUrl
              personNames,           // personNames array
              assignmentEventId      // assignmentEventId
            );
            groupEventId = groupMatchEvent.eventId;
            console.log('[Face Match] Created group match event:', groupEventId);

            // Add group event as pending so it appears immediately
            addPendingMatchEvent({
              ...groupMatchEvent,
              _isGroupEvent: true // Flag to identify as group event
            });
          } catch (err) {
            console.error('[Face Match] Failed to create group match event:', err);
            console.error('[Face Match] Error details:', err.response?.data);

            // CRITICAL: Don't create orphaned match events
            // Fallback: Create individual events WITHOUT group metadata
            console.warn('[Face Match] Falling back to individual events without group linkage');
            groupEventId = null;
          }
        }

        // Create all match events in parallel (no artificial delays)
        // Match each person with their corresponding bounding box from the matches array
        const eventPromises = matches.map((match) => {
          // Find the person data for this match using externalImageId
          const matchedPerson = matchedPersons.find(p => p.person_id === match.externalImageId);
          if (!matchedPerson) {
            console.error('[Face Match] No person found for match:', match.externalImageId);
            return Promise.resolve(null);
          }

          const personName = `${matchedPerson.first_name || ''} ${matchedPerson.last_name || ''}`.trim() || 'Unknown';

          // Determine if we should include group metadata
          // Only include if group event was successfully created
          const includeGroupMetadata = isMultiFace && groupEventId !== null;

          // Add optimistic event
          addPendingMatchEvent({
            personName,
            personImage: matchedPerson.image,
            person: matchedPerson,
            openAssignment,
            location,
            matchSource: 'face_match',
            matchAccuracy: match.similarity / 100,
            capturedImageUrl: result.captureS3Key,
            boundingBox: match.boundingBox, // Individual bounding box
            matchCount: includeGroupMetadata ? uniqueMatchedPersons : null,
            detectedFaceCount: includeGroupMetadata ? totalDetectedFaces : null,
            groupEventId: includeGroupMetadata ? groupEventId : null
          });

          // Create real match event
          return createMatchEvent(
            personName,
            matchedPerson.image,
            matchedPerson,
            openAssignment,
            location,
            'face_match',
            match.similarity / 100,
            includeGroupMetadata ? groupNote : null, // Only include group note if group event exists
            result.captureS3Key,
            match.boundingBox, // Individual bounding box
            includeGroupMetadata ? uniqueMatchedPersons : null,
            includeGroupMetadata ? totalDetectedFaces : null,
            includeGroupMetadata ? groupEventId : null,
            assignmentEventId  // assignmentEventId
          ).catch(err => {
            console.error(`[Face Match] Failed to create event for ${personName}:`, err);
            // Don't throw - we want other events to continue creating
            return null;
          });
        });

        // Wait for all events to complete (no throttling delay)
        await Promise.all(eventPromises);
      }

      // Handle no matches - trigger attribute search fallback
      if (matchCount === 0 && !silent && setTriggerAttributeSearch) {
        setTriggerAttributeSearch({ imageDataUrl: dataUrl });
      }

      // Call appropriate callback
      if (matchCount > 0 && onSuccess) {
        onSuccess(result, matchedPersons, matchList);
      } else if (matchCount === 0 && onNoMatch) {
        onNoMatch(captureId, dataUrl);
      }

      return {
        success: matchCount > 0,
        result,
        matchedPersons,
        matchList,
        topMatch: matchCount > 0 ? matches[0] : null
      };
    } catch (err) {
      console.error('[Face Match] Error:', {
        error: err.message,
        captureId
      });
      updateCapture(captureId, { matchFailed: true });

      // Call error callback
      if (onError) {
        onError(err, captureId);
      }

      throw err;
    }
  }, [
    silent,
    cognitoUser,
    compressionQuality,
    autoAdjustCompression,
    sliderValue,
    maxFacesValue,
    updateCharacteristicsOnMatch,
    autoCreateEventOnAIMatch,
    selectedDataset,
    setCompressionQuality,
    updateCapture,
    setFilteredPeople,
    setMatchSource,
    setMatchAccuracy,
    setEnumMap,
    setTriggerAttributeSearch,
    setCapturedImageS3Key,
    openAssignments,
    addPendingMatchEvent,
    onSuccess,
    onNoMatch,
    onError
  ]);

  return {
    matchFace
  };
}
